import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import { normalizeAuthoringDraft } from "../editor/authoringState";
import {
  selectActivePreviewMedium,
  selectChannelTransforms,
  selectFrameOutput,
  selectSelectedProductModel,
} from "../editor/messageDraftSelectors";
import type { MessageDraftState } from "../editor/messageDraftStore";
import { useMessageDraftStore } from "../editor/messageDraftStoreFactory";
import { buildTextComposerContributedVariantConfig } from "../frame/buildFrameUpdatePayload";
import type { PreviewFillParams } from "@/api/dragon-tiles/previewUserContext";

import { textPreviewQueryOptions } from "@/api/dragon-tiles";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

const TEXT_PREVIEW_DEBOUNCE_MS = 400;

export interface TextComposerPreviewContent {
  body: {
    type?: "TEXT" | "IMAGE" | "DOCUMENT";
    text?: string;
    url?: string;
  };
  buttons?: Array<{
    type?: "OPEN_URL";
    text?: string;
    url?: string;
  }>;
  suggestions?: Array<{
    type?: string;
    text?: string;
    url?: string;
  }>;
}

export function useMessagePreview(options?: {
  siteId?: string;
  previewConfig?: Record<string, unknown>;
  previewFillParams?: PreviewFillParams;
}) {
  const authoringState = useMessageDraftStore((s) => s.authoringState);
  const previewRecipientViewMode = useMessageDraftStore(
    (s) => s.previewRecipientViewMode,
  );
  const dragonTileCategory = useMessageDraftStore((s) => s.dragonTileCategory);

  const draftSnapshot: MessageDraftState = useMemo(
    () => ({ authoringState, previewRecipientViewMode, dragonTileCategory }),
    [authoringState, previewRecipientViewMode, dragonTileCategory],
  );

  const frameOutput = useMemo(
    () => selectFrameOutput(draftSnapshot),
    [draftSnapshot],
  );
  const channelTransforms = useMemo(
    () => selectChannelTransforms(draftSnapshot),
    [draftSnapshot],
  );
  const activePreviewMedium = useMemo(
    () => selectActivePreviewMedium(draftSnapshot),
    [draftSnapshot],
  );

  const selectedProductModel = useMemo(
    () => selectSelectedProductModel(draftSnapshot),
    [draftSnapshot],
  );

  const previewConfig = useMemo(() => {
    const contributedConfig = buildTextComposerContributedVariantConfig({
      authoringState: normalizeAuthoringDraft(authoringState),
      productModel: selectedProductModel,
    });
    return { ...contributedConfig, ...options?.previewConfig };
  }, [authoringState, options?.previewConfig, selectedProductModel]);

  const channelPreviewInputs = useMemo(() => {
    const byMedium: Partial<
      Record<
        "sms" | "mms" | "rcs",
        (typeof frameOutput.frame.frame_variants)[number]["content_json"]
      >
    > = {};
    for (const variant of frameOutput.frame.frame_variants) {
      if (
        variant.medium === "sms" ||
        variant.medium === "mms" ||
        variant.medium === "rcs"
      ) {
        byMedium[variant.medium] = variant.content_json;
      }
    }
    return byMedium;
  }, [frameOutput]);

  const debouncedChannelPreviewInputs = useDebouncedValue(
    channelPreviewInputs,
    TEXT_PREVIEW_DEBOUNCE_MS,
  );

  const smsContentJson = debouncedChannelPreviewInputs.sms;
  const mmsContentJson = debouncedChannelPreviewInputs.mms;
  const rcsContentJson = debouncedChannelPreviewInputs.rcs;

  const createDisabledPreviewQuery = useCallback(
    (medium: "sms" | "mms" | "rcs") => ({
      queryKey: ["textPreview", options?.siteId ?? "", "disabled", medium],
      queryFn: () =>
        Promise.resolve({
          medium,
          transformedContent: {
            body: {},
          },
        }),
      enabled: false,
    }),
    [options?.siteId],
  );

  const smsPreviewQuery = useQuery({
    ...(smsContentJson
      ? textPreviewQueryOptions(
          options?.siteId ?? "",
          { medium: "sms", content_json: smsContentJson },
          previewConfig,
          options?.previewFillParams,
        )
      : createDisabledPreviewQuery("sms")),
    enabled:
      (options?.siteId?.length ?? 0) > 0 &&
      smsContentJson !== undefined &&
      activePreviewMedium === "sms",
  });

  const mmsPreviewQuery = useQuery({
    ...(mmsContentJson
      ? textPreviewQueryOptions(
          options?.siteId ?? "",
          { medium: "mms", content_json: mmsContentJson },
          previewConfig,
          options?.previewFillParams,
        )
      : createDisabledPreviewQuery("mms")),
    enabled:
      (options?.siteId?.length ?? 0) > 0 &&
      mmsContentJson !== undefined &&
      activePreviewMedium === "mms",
  });

  const rcsPreviewQuery = useQuery({
    ...(rcsContentJson
      ? textPreviewQueryOptions(
          options?.siteId ?? "",
          { medium: "rcs", content_json: rcsContentJson },
          previewConfig,
          options?.previewFillParams,
        )
      : createDisabledPreviewQuery("rcs")),
    enabled:
      (options?.siteId?.length ?? 0) > 0 &&
      rcsContentJson !== undefined &&
      activePreviewMedium === "rcs",
  });

  const [lastResolvedPreviewByChannel, setLastResolvedPreviewByChannel] =
    useState<{
      RCS: TextComposerPreviewContent | null;
      SMS: TextComposerPreviewContent | null;
      MMS: TextComposerPreviewContent | null;
    }>({
      RCS: null,
      SMS: null,
      MMS: null,
    });

  useEffect(() => {
    setLastResolvedPreviewByChannel((current) => ({
      RCS: rcsPreviewQuery.data?.transformedContent ?? current.RCS,
      SMS: smsPreviewQuery.data?.transformedContent ?? current.SMS,
      MMS: mmsPreviewQuery.data?.transformedContent ?? current.MMS,
    }));
  }, [
    mmsPreviewQuery.data?.transformedContent,
    rcsPreviewQuery.data?.transformedContent,
    smsPreviewQuery.data?.transformedContent,
  ]);

  const previewByChannel = useMemo(
    () =>
      options?.siteId
        ? {
            RCS:
              rcsPreviewQuery.data?.transformedContent ??
              lastResolvedPreviewByChannel.RCS,
            SMS:
              smsPreviewQuery.data?.transformedContent ??
              lastResolvedPreviewByChannel.SMS,
            MMS:
              mmsPreviewQuery.data?.transformedContent ??
              lastResolvedPreviewByChannel.MMS,
          }
        : {
            RCS: channelTransforms.rcs?.content ?? null,
            SMS: channelTransforms.sms?.content ?? null,
            MMS: channelTransforms.mms?.content ?? null,
          },
    [
      channelTransforms,
      lastResolvedPreviewByChannel.MMS,
      lastResolvedPreviewByChannel.RCS,
      lastResolvedPreviewByChannel.SMS,
      mmsPreviewQuery.data?.transformedContent,
      rcsPreviewQuery.data?.transformedContent,
      smsPreviewQuery.data?.transformedContent,
      options?.siteId,
    ],
  );

  const isPreviewLoading =
    (activePreviewMedium === "sms" && smsPreviewQuery.isFetching) ||
    (activePreviewMedium === "mms" && mmsPreviewQuery.isFetching) ||
    (activePreviewMedium === "rcs" && rcsPreviewQuery.isFetching);

  const activePreviewQuery =
    activePreviewMedium === "sms"
      ? smsPreviewQuery
      : activePreviewMedium === "mms"
        ? mmsPreviewQuery
        : activePreviewMedium === "rcs"
          ? rcsPreviewQuery
          : null;

  const refetchPreview = useCallback(() => {
    if (activePreviewMedium === "sms") void smsPreviewQuery.refetch();
    else if (activePreviewMedium === "mms") void mmsPreviewQuery.refetch();
    else if (activePreviewMedium === "rcs") void rcsPreviewQuery.refetch();
  }, [activePreviewMedium, mmsPreviewQuery, rcsPreviewQuery, smsPreviewQuery]);

  const previewError =
    activePreviewMedium === "sms"
      ? smsPreviewQuery.error
      : activePreviewMedium === "mms"
        ? mmsPreviewQuery.error
        : activePreviewMedium === "rcs"
          ? rcsPreviewQuery.error
          : null;

  return {
    previewByChannel,
    previewMeta: {
      isLoading: isPreviewLoading,
      isError: Boolean(activePreviewQuery?.isError),
      error: previewError,
      isEnabled: Boolean(options?.siteId),
    },
    refetchPreview,
  };
}
